import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CheckCircle2, ChevronRight, Loader2, Undo2, ExternalLink, Inbox } from "lucide-react";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const money = (cur, v) => `${cur || ""} ${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: cur === "TZS" ? 0 : 2 })}`;
const pct = (v) => `${(Number(v || 0) * 100).toFixed(1)}%`;
const when = (s) => (s ? new Date(s).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
const STATUS = { pending: "Awaiting You", approved: "Approved", sent_back: "Sent Back" };

function ApprovalCard({ a, onOpen }) {
  return (
    <button onClick={onOpen} className="flex w-full items-center gap-3 rounded-xl border border-border bg-white p-4 text-left transition-shadow hover:shadow-sm" data-testid={`approval-card-${a.id}`}>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2"><span className="font-mono text-xs font-semibold text-[#1B4383]">{a.rfq_code}</span><span className="text-[11px] text-muted-foreground">{when(a.requested_at)} · {a.requested_by_name}</span></div>
        <div className="truncate font-semibold text-slate-900">{a.client_name || a.rfq_title}</div>
        <div className="truncate text-xs text-muted-foreground">{a.supplier_name}</div>
      </div>
      <div className="text-right">
        <div className="font-mono text-sm font-semibold text-slate-900">{money(a.currency, a.totals?.sell)}</div>
        <div className="text-xs text-muted-foreground">{pct(a.totals?.margin_pct)} margin</div>
        {a.status !== "pending" && <div className={`text-[11px] font-semibold ${a.status === "approved" ? "text-[#1B4383]" : "text-red-700"}`}>{STATUS[a.status]}</div>}
      </div>
      <ChevronRight className="h-4 w-4 text-slate-300" />
    </button>
  );
}

function Totals({ a }) {
  const t = a.snapshot?.totals || {};
  const cells = [["Total Cost", money(a.currency, t.cost)], ["Total Sell", money(a.currency, t.sell)], ["Profit", money(a.currency, t.profit)], ["Margin", pct(t.margin_pct)]];
  if (t.freight) cells.splice(1, 0, ["Freight", money(a.currency, t.freight)]);
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-5" data-testid="approval-totals">
      {cells.map(([l, v]) => <div key={l} className="rounded-lg border border-border bg-white px-3 py-2"><div className="text-[11px] text-muted-foreground">{l}</div><div className="font-mono text-sm font-semibold text-slate-900">{v}</div></div>)}
    </div>
  );
}

function Lines({ a }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-white">
      <table className="w-full text-sm" data-testid="approval-lines">
        <thead className="bg-slate-50 text-[11px] text-slate-500"><tr><th className="px-3 py-2 text-left">Item</th><th className="px-2 py-2 text-right">Qty</th><th className="px-2 py-2 text-right">Cost</th><th className="px-2 py-2 text-right">Sell / Unit</th><th className="px-2 py-2 text-right">Sell</th><th className="px-3 py-2 text-right">Margin</th></tr></thead>
        <tbody>{(a.snapshot?.lines || []).map((l, i) => (
          <tr key={i} className="border-t border-border"><td className="px-3 py-2 text-slate-800">{l.description}</td><td className="px-2 py-2 text-right">{l.qty}</td><td className="px-2 py-2 text-right font-mono">{money("", l.cost)}</td><td className="px-2 py-2 text-right font-mono">{money("", l.sell_unit)}</td><td className="px-2 py-2 text-right font-mono">{money("", l.sell)}</td><td className={`px-3 py-2 text-right font-mono ${l.margin_pct < 0.1 ? "text-red-700" : "text-slate-800"}`}>{pct(l.margin_pct)}</td></tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function Decide({ a, onDone }) {
  const [note, setNote] = useState("");
  const [margin, setMargin] = useState("");
  const [busy, setBusy] = useState("");
  const go = async (action) => {
    setBusy(action);
    try {
      const { data } = await api.post(`/costing-approvals/${a.id}/decide`, { action, note, margin_pct: margin === "" ? null : Number(margin) });
      toast.success(action === "approve" ? "Costing approved" : "Sent back to Sales"); window.dispatchEvent(new Event("somcore:alerts")); onDone(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  return (
    <div className="space-y-3 rounded-xl border border-border bg-white p-4" data-testid="approval-decide">
      <div className="flex flex-wrap items-end gap-3">
        <div><div className="mb-1 text-xs text-muted-foreground">Change Margin (optional)</div>
          <div className="flex items-center gap-1"><Input type="number" value={margin} onChange={(e) => setMargin(e.target.value)} placeholder={a.snapshot?.margin_pct != null ? String(Math.round(a.snapshot.margin_pct * 1000) / 10) : ""} className="h-9 w-24" data-testid="approval-margin" /><span className="text-sm text-slate-500">%</span></div></div>
        <Textarea rows={1} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note for Sales (needed to send back)" className="min-h-9 w-full sm:w-auto sm:flex-1" data-testid="approval-note" />
      </div>
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => go("approve")} disabled={!!busy} className="flex-1 bg-[#1B4383] hover:bg-[#16386e] sm:flex-none" data-testid="approval-approve">{busy === "approve" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}{margin !== "" ? `Approve At ${margin}%` : "Approve"}</Button>
        <Button variant="outline" onClick={() => go("send_back")} disabled={!!busy} className="flex-1 sm:flex-none" data-testid="approval-send-back">{busy === "send_back" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Undo2 className="mr-2 h-4 w-4" />}Send Back</Button>
      </div>
    </div>
  );
}

function Detail({ id }) {
  const nav = useNavigate();
  const [a, setA] = useState(null);
  useEffect(() => { api.get(`/costing-approvals/${id}`).then(({ data }) => setA(data)).catch((e) => toast.error(apiError(e))); }, [id]);
  if (!a) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  return (
    <div className="mx-auto max-w-4xl space-y-4" data-testid="approval-detail">
      <button onClick={() => nav("/approvals")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-testid="approval-back"><ArrowLeft className="h-4 w-4" />All Approvals</button>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="font-mono text-xs font-semibold text-[#1B4383]">{a.rfq_code} · Rev {a.revision}</div>
          <h2 className="font-heading text-xl font-bold text-slate-900">{a.client_name || a.rfq_title}</h2>
          <div className="text-sm text-muted-foreground">{a.supplier_name} · asked by {a.requested_by_name} {when(a.requested_at)}</div>
        </div>
        <Button variant="outline" size="sm" onClick={() => nav(`/rfqs/${a.rfq_id}/quotes/${a.supplier_id}`)} data-testid="approval-open-costing"><ExternalLink className="mr-1.5 h-4 w-4" />Full Costing</Button>
      </div>
      {a.note && <div className="rounded-lg bg-[#F5F8FD] px-3 py-2 text-sm text-slate-700" data-testid="approval-request-note">"{a.note}"</div>}
      <Totals a={a} />
      <Lines a={a} />
      {a.status === "pending" ? <Decide a={a} onDone={setA} />
        : <div className="rounded-lg border border-border bg-white px-4 py-3 text-sm" data-testid="approval-outcome"><b>{STATUS[a.status]}</b> by {a.decided_by_name} {when(a.decided_at)}{a.new_margin_pct != null ? ` · margin set to ${a.new_margin_pct}%` : ""}{a.decision_note ? ` · "${a.decision_note}"` : ""}</div>}
    </div>
  );
}

export default function Approvals() {
  const { id } = useParams();
  const nav = useNavigate();
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState(null);
  const load = useCallback(() => { setRows(null); api.get("/costing-approvals", { params: { status: tab } }).then(({ data }) => setRows(data)).catch((e) => { toast.error(apiError(e)); setRows([]); }); }, [tab]);
  useEffect(() => { if (!id) { load(); } }, [id, load]);
  return (
    <AppLayout title="Costing Approvals">
      {id ? <Detail id={id} /> : (
        <div className="mx-auto max-w-3xl space-y-4">
          <div className="inline-flex rounded-lg border border-border bg-white p-1">
            {[["pending", "Awaiting You"], ["all", "History"]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} className={`rounded-md px-4 py-1.5 text-sm font-semibold transition-colors ${tab === k ? "bg-[#1B4383] text-white" : "text-slate-600 hover:bg-slate-50"}`} data-testid={`approvals-tab-${k}`}>{l}</button>
            ))}
          </div>
          {rows === null ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
            : rows.length === 0 ? <div className="flex flex-col items-center gap-2 py-16 text-sm text-muted-foreground" data-testid="approvals-empty"><Inbox className="h-8 w-8 text-slate-300" />{tab === "pending" ? "Nothing waiting for you." : "No approvals yet."}</div>
            : <div className="space-y-2">{rows.map((a) => <ApprovalCard key={a.id} a={a} onOpen={() => nav(`/approvals/${a.id}`)} />)}</div>}
        </div>
      )}
    </AppLayout>
  );
}
